/** Tenant id lists — the one wire format for every multi-tenant filter (plan D1, D2, D5).
 *
 * A tenant filter is ONE comma-joined param, ids ascending and de-duplicated:
 * `?tenant=12,845`, `?scope=12,845`, `tour_operator_id=12,845`. One id is a one-element
 * list, so every existing single-tenant link, preset and deep link keeps working.
 *
 * The URL and preset layer stays string-typed; ids are parsed only at the edge — in a
 * `FilterDef.toApi` (`tenantIdsToApi`) and in the picker value (D2).
 *
 * Sentinels (`system`, `null`, `none`, Internal `0`, `all`) are single tokens that name
 * no operator; `parseTenantIds` drops them, and `tenantIdsToApiWith` passes the listed
 * ones through only when they stand alone (D3 — the backend answers a mix with 400).
 *
 * `singleTenantId` is THE single-tenant predicate (D5): every single-tenant consumer —
 * create dialogs, workspaces, drag reorder, category pickers, batch value pickers,
 * export — acts only when the list names exactly one operator.
 */

/** A tenant filter as an API module takes it: one id (`12`) or a comma list (`'12,845'`),
 *  plus whatever sentinel the endpoint knows. Modules serialise it with `String(v)`. */
export type TenantIdsParam = number | string;

const ID_TOKEN = /^\d+$/;

/** Sorted, de-duplicated positive ids. */
function normalise(ids: Iterable<number>): number[] {
  const out = new Set<number>();
  for (const id of ids) {
    if (Number.isSafeInteger(id) && id > 0) out.add(id);
  }
  return [...out].sort((a, b) => a - b);
}

/** `raw` → the operator ids it names, ascending and de-duplicated.
 *
 *  Accepts a comma list (`'845,12,12'` → `[12, 845]`), a single id, or ids already
 *  parsed. Tokens that are not positive integers — sentinels, `0`, garbage from a
 *  hand-edited URL — are dropped, so a sentinel or nonsense yields `[]`. */
export function parseTenantIds(
  raw: string | number | readonly number[] | null | undefined,
): number[] {
  if (raw === null || raw === undefined || raw === '') return [];
  if (typeof raw === 'number') return normalise([raw]);
  if (typeof raw !== 'string') return normalise(raw);
  const ids: number[] = [];
  for (const token of raw.split(',')) {
    const t = token.trim();
    if (ID_TOKEN.test(t)) ids.push(Number(t));
  }
  return normalise(ids);
}

/** Ids → the wire value (`'12,845'`), sorted and de-duplicated; null for none. */
export function formatTenantIds(ids: readonly number[]): string | null {
  const sorted = normalise(ids);
  return sorted.length ? sorted.join(',') : null;
}

/** The single-tenant predicate (D5): the id when the list names exactly one operator,
 *  else null (none, several, or a sentinel). */
export function singleTenantId(
  idsOrRaw: string | readonly number[] | null | undefined,
): number | null {
  const ids = parseTenantIds(idsOrRaw);
  return ids.length === 1 ? (ids[0] ?? null) : null;
}

/** The `FilterDef.toApi` of a tenant filter: the URL value → `'12,845'`, or undefined
 *  (no filter) when it names no operator. Replaces the per-page `digits` helpers. */
export function tenantIdsToApi(raw: string): string | undefined {
  return formatTenantIds(parseTenantIds(raw)) ?? undefined;
}

/** `tenantIdsToApi` for a filter that also carries sentinels of its own
 *  (`tenantIdsToApiWith(['system'])`, `tenantIdsToApiWith(['null'])`, AI spend's `['0']`).
 *  A listed sentinel passes through only as the whole value; mixed with ids it is
 *  dropped and the ids stay (sentinels are exclusive, D3). */
export function tenantIdsToApiWith(
  sentinels: readonly string[],
): (raw: string) => string | undefined {
  return (raw) => {
    const t = raw.trim();
    if (sentinels.includes(t)) return t;
    return tenantIdsToApi(t);
  };
}
